import { lessonHref } from "@lib/curriculum/paths";
import { ROUTES } from "@lib/routes";

export type CurriculumBackLabelKey = "curriculum.back.toCurriculum" | "curriculum.back.toLesson";

export type CurriculumBackNav = {
  href: string;
  labelKey: CurriculumBackLabelKey;
};

type SearchParamsLike = {
  get(name: string): string | null;
};

function buildLessonHref(lessonId: string, searchParams?: SearchParamsLike | null): string {
  return lessonHref(
    { lessonId },
    {
      contentParam: searchParams?.get("content") ?? undefined,
      versionParam: searchParams?.get("version") ?? undefined,
    },
  );
}

/**
 * The curriculum URL tree is one segment deep: /curriculum/{lessonId}. Only the
 * lesson-runner sub-pages (flashcards/homework under a lesson) keep their back
 * target to the lesson page - with content/version param passthrough intact so
 * review-mode deep links survive. Everything else returns to the board.
 */
export function getCurriculumBackNav(
  pathname: string,
  searchParams?: SearchParamsLike | null,
): CurriculumBackNav | null {
  const parts = pathname.split("/").filter(Boolean);
  if (parts[0] !== "curriculum") return null;

  const depth = parts.length - 1;
  if (depth <= 0) return null;

  const lessonId = parts[1];
  const subPage = parts[2];

  // The lesson archive is a static sibling of the dynamic segment, beside the board.
  if (lessonId === "history") {
    return { href: ROUTES.curriculum, labelKey: "curriculum.back.toCurriculum" };
  }

  if (depth === 2 && (subPage === "flashcards" || subPage === "homework")) {
    return {
      href: buildLessonHref(lessonId, searchParams),
      labelKey: "curriculum.back.toLesson",
    };
  }

  if (depth >= 3) {
    return null;
  }

  // Depth 1 (the lesson runner itself) returns to the board.
  return { href: ROUTES.curriculum, labelKey: "curriculum.back.toCurriculum" };
}
